import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StageChip } from "@/components/StageChip";
import { EditableField } from "@/components/EditableField";
import { TruvaHouseLink } from "@/components/TruvaHouseLink";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DocumentsTab } from "@/components/DocumentsTab";
import { CaseSummary } from "@/components/CaseSummary";
import { TimelineTab } from "@/components/TimelineTab";
import { WhatsAppTab } from "@/components/WhatsAppTab";
import { WaButton, useWaBlock } from "@/components/WaButton";
import { telLink } from "@/lib/phone";
import { FIELDS as FF, LOST_STAGE, DISBURSED_OR_LATER } from "@/lib/fields";
import { formatINR, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, FileText, ExternalLink, FolderOpen, Eye,
  Loader2, Copy, Check, X, ChevronRight, History as HistoryIcon, RefreshCw, Info, MoreHorizontal, MessageCircle, ChevronDown,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function WaMenu({ wa, phone, onGoWhatsApp }) {
  const cls = "flex items-center gap-1.5 text-sm rounded-lg px-3 py-1.5 transition-colors";
  if (!wa) return <span className={`${cls} border border-[#E2E2E2] text-[#C5C4C4]`} data-testid="whatsapp-link">WhatsApp</span>;
  const direct = wa.direct_link || (phone && telLink(phone) ? `https://wa.me/${telLink(phone).replace(/\D/g, "")}` : null);
  if (!wa.customer_open_link) {
    return <button onClick={onGoWhatsApp} data-testid="whatsapp-link" className={`${cls} border border-[#F5B71D]/60 text-[#92600a] bg-[#F5B71D]/10 hover:bg-[#F5B71D]/20`}><MessageCircle className="w-4 h-4" /> Link WhatsApp group</button>;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="whatsapp-link" className={`${cls} text-[#159A50] border border-[#159A50]/30 hover:bg-[#159A50]/5`}><MessageCircle className="w-4 h-4" /> WhatsApp <ChevronDown className="w-3.5 h-3.5" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white rounded-xl border-[#E2E2E2] text-sm">
        <DropdownMenuItem onClick={() => window.open(wa.customer_open_link, "_blank")} data-testid="wa-menu-group">{wa.has_customer_group && !wa.prefers_1to1 ? "Open customer group" : "Open chat"}</DropdownMenuItem>
        {direct && direct !== wa.customer_open_link && <DropdownMenuItem onClick={() => window.open(direct, "_blank")} data-testid="wa-menu-direct">Message customer directly (1:1)</DropdownMenuItem>}
        <DropdownMenuItem onClick={onGoWhatsApp} data-testid="wa-menu-tab">Open WhatsApp tab (history, drafts)</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Info as Hint } from "@/components/Info";
import { StuckBadge, ExtendDialog } from "@/components/Tat";
import { Loading } from "@/components/Loading";
import { NextActionField } from "@/components/NextActionField";
import { EscalationsTab } from "@/components/EscalationsTab";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const WS_TABS = ["Summary", "Overview", "Documents", "Timeline", "WhatsApp", "Escalations", "Tools", "Internal", "Actions"];

export default function CaseWorkspace() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const { user, activeRole } = useAuth();
  const [c, setC] = useState(null);
  const [selects, setSelects] = useState({});
  const [stageGroups, setStageGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Summary");
  const [wa, reloadWa] = useWaBlock(recordId);
  const [extendOpen, setExtendOpen] = useState(false);
  useEffect(() => { window.__truvaCaseId = c?.case_id || ""; return () => { window.__truvaCaseId = ""; }; }, [c?.case_id]);
  const [conflict, setConflict] = useState(null); // {fields, saveFn}
  const canEdit = ["RM", "Manager", "Admin"].includes(activeRole);
  const truvaHouse = c && c.fields?.[FF.TRUVA_NONTRUVA] === "Yes" &&
    ((Array.isArray(c.fields?.[FF.PROPERTY_TRUVA]) && c.fields[FF.PROPERTY_TRUVA].length > 0) || !!c.property_truva_name);
  const wsTabs = truvaHouse ? [...WS_TABS.slice(0, 5), "Truva House", ...WS_TABS.slice(5)] : WS_TABS;

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const [{ data }, meta] = await Promise.all([
        api.get(`/cases/${recordId}`, { params: { refresh } }),
        api.get("/meta/selects"),
      ]);
      setC(data);
      setSelects(meta.data.selects || {});
      setStageGroups(meta.data.stage_groups || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load case");
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => { load(); }, [load]);

  const f = (id) => c?.fields?.[id];

  // core save with conflict handling
  const doSave = async (fields, { force = false, logConversation = false } = {}) => {
    const expected_values = {};
    Object.keys(fields).forEach((k) => { expected_values[k] = c.fields?.[k] ?? null; });
    const { data } = await api.patch(`/cases/${recordId}`, { fields, expected_values, force, log_conversation: logConversation });
    if (data.conflict) {
      setConflict({ fields, conflictFields: data.conflict_fields || [] });
      return false;
    }
    setC(data);
    toast.success(data.logged_conversation ? "Saved · Last Conversation set to today" : "Synced to Airtable");
    return true;
  };

  const saveField = async (fieldId, value, opts = {}) => {
    try {
      return await doSave({ [fieldId]: value }, opts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return false;
    }
  };

  const resolveConflict = async (mode) => {
    const fields = conflict.fields;
    setConflict(null);
    if (mode === "reload") { await load(true); return; }
    try {
      const { data } = await api.patch(`/cases/${recordId}`, {
        fields, force: true,
      });
      setC(data);
      toast.success("Saved (overwrote Airtable change)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  if (!c) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-6 py-8"><Loading label="Loading case…" rows={8} onRetry={() => load(true)} section="Case page" /></div>
      </Layout>
    );
  }

  const name = f(FF.BORROWER) || f(FF.NAME);
  const stage = f(FF.LEAD_STAGE);
  const disbursedEditable = DISBURSED_OR_LATER.includes(stage);
  const caseRm = Array.isArray(f(FF.HL_RM)) ? f(FF.HL_RM).join(", ") : f(FF.HL_RM);
  const othersCase = activeRole === "RM" && user?.hl_rm_value && caseRm && caseRm !== user.hl_rm_value;
  const truvaVal = f(FF.TRUVA_NONTRUVA);
  const truvaLabel = truvaVal === "Yes" || truvaVal === "Truva" ? "Truva"
    : truvaVal === "No" || truvaVal === "Non Truva" ? "Out of Truva" : null;

  return (
    <Layout>
      <button onClick={() => navigate("/cases")} data-testid="back-button"
        className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#333333] mb-3 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to cases
      </button>

      {/* Header */}
      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <span className="font-mono text-sm font-semibold text-[#E2521B] bg-[#FF4802]/5 px-2.5 py-1 rounded-lg border border-[#FF4802]/20" data-testid="case-header-id">{c.case_id}</span>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-[#333333]">{name || "—"}</h1>
              <div className="flex items-center gap-3 mt-1 text-sm text-[#6B7280]">
                <span className="flex items-center gap-1">HL RM: <span className="font-medium text-[#333333]">{Array.isArray(f(FF.HL_RM)) ? f(FF.HL_RM).join(", ") : f(FF.HL_RM)}</span> <Hint k="hl_rm" /></span>
                {truvaLabel && <><span>·</span><span data-testid="header-truva">{truvaLabel}</span></>}
              </div>
              {othersCase && (
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[#92600a] bg-[#F5B71D]/15 border border-[#F5B71D]/40 rounded-full px-2.5 py-0.5" data-testid="assigned-to-label">
                  <Info className="w-3.5 h-3.5" /> Assigned to {caseRm} — you're viewing another RM's case
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="case-header-actions">
            {f(FF.PHONE) && (
              telLink(f(FF.PHONE)) ? (
                <a href={telLink(f(FF.PHONE))} data-testid="call-link" className="flex items-center gap-1.5 text-sm text-[#333333] border border-[#E2E2E2] rounded-lg px-3 py-1.5 hover:bg-[#F7F7F7] transition-colors"><Phone className="w-4 h-4" /> Call</a>
              ) : (
                <span title="Phone number looks invalid" data-testid="call-link-invalid" className="flex items-center gap-1.5 text-sm text-[#C5C4C4] border border-[#E2E2E2] rounded-lg px-3 py-1.5 cursor-not-allowed"><Phone className="w-4 h-4" /> Call</span>
              )
            )}
            <WaMenu wa={wa} phone={f(FF.PHONE)} onGoWhatsApp={() => setTab("WhatsApp")} />
            <StageChip stage={stage} />
            {f(FF.FOLLOW_UP_DATE) && f(FF.FOLLOW_UP_DATE).slice(0, 10) < new Date().toISOString().slice(0, 10) && c.stage_group === "active" && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-[#CA1212]/10 text-[#CA1212] border border-[#CA1212]/30 font-medium" data-testid="header-overdue">Follow-up overdue · {fmtDate(f(FF.FOLLOW_UP_DATE))}</span>
            )}
            {c.escalations_open > 0 && <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#CA1212]/10 text-[#CA1212] border border-[#CA1212]/30 font-medium cursor-pointer" onClick={() => setTab("Escalations")} data-testid="escalation-open-chip">Escalation open</span>}
            {c.tat && <StuckBadge tat={c.tat} caseId={c.case_id} onExtend={null} />}
            {extendOpen && <ExtendDialog recordId={recordId} tat={c.tat} onClose={() => setExtendOpen(false)} onDone={() => load(true)} />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="case-more-menu" className="p-2 rounded-lg border border-[#E2E2E2] hover:bg-[#F7F7F7] transition-colors" title="More"><MoreHorizontal className="w-4 h-4 text-[#6B7280]" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white rounded-xl border-[#E2E2E2] text-sm">
                <DropdownMenuItem onClick={() => load(true)} data-testid="case-refresh">Refresh case</DropdownMenuItem>
                {canEdit && c.tat?.stuck && <DropdownMenuItem onClick={() => setExtendOpen(true)} data-testid="case-extend-tat">Extend TAT…</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => setTab("Escalations")} data-testid="case-menu-escalate">Raise escalation</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTab("Actions")} data-testid="case-menu-reassign">Request reassignment</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }} data-testid="case-menu-copy">Copy case link</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-[#E2E2E2]/50 p-1 rounded-xl w-fit mb-5">
        {wsTabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`ws-tab-${t.toLowerCase().replace(/ /g, "-")}`}
            className={`text-xs px-4 py-1.5 rounded-lg transition-all font-medium ${tab === t ? "bg-white text-[#333333] shadow-sm" : "text-[#6B7280] hover:text-[#333333]"}`}>{t}{t === "Escalations" && c.escalations_open > 0 && <span className="ml-1 bg-[#CA1212] text-white rounded-full px-1.5 text-[10px]" data-testid="escalations-badge">{c.escalations_open}</span>}</button>
        ))}
      </div>

      <ErrorBoundary name={tab} resetKey={`${tab}:${recordId}`}>
      {tab === "Summary" && (
        <CaseSummary c={c} recordId={recordId} saveField={saveField} selects={selects} wa={wa}
          canEdit={canEdit} reload={load} onGoTimeline={() => setTab("Timeline")} onGoWhatsApp={() => setTab("WhatsApp")} />
      )}
      {tab === "Overview" && (
        <OverviewTab c={c} f={f} selects={selects} stageGroups={stageGroups} saveField={saveField}
          canEdit={canEdit} disbursedEditable={disbursedEditable} onStageChange={load} recordId={recordId} />
      )}
      {tab === "Documents" && <DocumentsTab c={c} recordId={recordId} saveField={saveField} canEdit={canEdit} />}
      {tab === "Tools" && <ToolsTab c={c} f={f} />}
      {tab === "Truva House" && <TruvaHouseTab c={c} f={f} />}
      {tab === "Timeline" && <TimelineTab recordId={recordId} />}
      {tab === "WhatsApp" && <WhatsAppTab recordId={recordId} onChanged={reloadWa} />}
      {tab === "Escalations" && <EscalationsTab recordId={recordId} canEdit={canEdit} onChanged={() => load(true)} />}
      {tab === "Internal" && <InternalTab f={f} selects={selects} saveField={saveField} canEdit={canEdit} />}
      {tab === "Actions" && <ActionsTab c={c} f={f} recordId={recordId} />}
      </ErrorBoundary>

      {/* Conflict dialog */}
      <Dialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <DialogContent className="bg-white rounded-2xl truva-popup-shadow" aria-describedby={undefined} data-testid="conflict-dialog">
          <DialogHeader><DialogTitle className="text-[#333333] font-medium">Case changed in Airtable</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7280] leading-relaxed">This case was changed in Airtable since you opened it. Reload the latest data, or save your change anyway (overwrites).</p>
          <DialogFooter className="gap-2">
            <button onClick={() => resolveConflict("reload")} data-testid="conflict-reload" className="px-4 py-2 rounded-lg border border-[#E2E2E2] text-sm text-[#333333] hover:bg-[#F7F7F7] transition-colors">Reload</button>
            <button onClick={() => resolveConflict("force")} data-testid="conflict-force" className="px-4 py-2 rounded-lg bg-[#FF4802] text-white text-sm hover:bg-[#E2521B] transition-colors font-medium">Save my change anyway</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white p-6 shadow-sm" data-testid={testid}>
      <h3 className="text-xs font-medium text-[#6B7280] mb-4 uppercase" style={{ letterSpacing: "0.06em" }}>{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">{children}</div>
    </div>
  );
}

function OverviewTab({ c, f, selects, stageGroups, saveField, canEdit, disbursedEditable, onStageChange, recordId }) {
  const [stageOpen, setStageOpen] = useState(false);
  return (
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-12 lg:col-span-8 space-y-6">
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm flex items-center justify-between">
          <div>
            <span className="text-xs font-medium uppercase text-[#6B7280]" style={{ letterSpacing: "0.06em" }}>Lead Stage</span>
            <div className="mt-1"><StageChip stage={f(FF.LEAD_STAGE)} /></div>
          </div>
          {canEdit && (
            <button onClick={() => setStageOpen(true)} data-testid="stage-change-button"
              className="flex items-center gap-1.5 text-sm bg-[#FF4802] text-white px-4 py-2 rounded-lg hover:bg-[#E2521B] transition-colors font-medium">
              Change stage <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        <Section title="Customer">
          <EditableField label="Email ID" value={f(FF.EMAIL)} fieldId={FF.EMAIL} editable={canEdit} onSave={saveField} />
          <EditableField label="Borrower (Full Name)" value={f(FF.BORROWER)} fieldId={FF.BORROWER} editable={canEdit} onSave={saveField} />
          <EditableField label="Co-Borrower" value={f(FF.CO_BORROWER)} fieldId={FF.CO_BORROWER} editable={canEdit} onSave={saveField} />
          <EditableField label="Co-Borrower in Agreement?" value={f(FF.CO_BORROWER_AGREEMENT)} fieldId={FF.CO_BORROWER_AGREEMENT} type="select" options={selects[FF.CO_BORROWER_AGREEMENT] || ["Yes", "No"]} editable={canEdit} onSave={saveField} />
          <EditableField label="Truva / Non Truva" value={f(FF.TRUVA_NONTRUVA) === "Yes" ? "Truva" : f(FF.TRUVA_NONTRUVA) === "No" ? "Out of Truva" : f(FF.TRUVA_NONTRUVA)} fieldId={FF.TRUVA_NONTRUVA} editable={false} onSave={saveField} />
        </Section>

        <Section title="Loan">
          <EditableField label="Loan amount required" value={f(FF.LOAN_AMOUNT)} fieldId={FF.LOAN_AMOUNT} type="number" editable={canEdit} onSave={saveField} render={(v) => formatINR(v)} />
          <EditableField label="Loan Tenure" value={f(FF.LOAN_TENURE)} fieldId={FF.LOAN_TENURE} type="number" editable={canEdit} onSave={saveField} />
          <EditableField label="Agreement Value" value={f(FF.AGREEMENT_VALUE)} fieldId={FF.AGREEMENT_VALUE} type="number" editable={canEdit} onSave={saveField} render={(v) => formatINR(v)} />
          <EditableField label="Bank Name (considered)" value={f(FF.BANK_NAME)} fieldId={FF.BANK_NAME} type="multiselect" options={selects[FF.BANK_NAME] || []} editable={canEdit} onSave={saveField} />
          <EditableField label="Bank Type" value={f(FF.BANK_TYPE)} fieldId={FF.BANK_TYPE} type="select" options={selects[FF.BANK_TYPE] || []} editable={canEdit} onSave={saveField} />
          <EditableField label="Partner Company" value={f(FF.PARTNER_COMPANY)} fieldId={FF.PARTNER_COMPANY} type="select" options={selects[FF.PARTNER_COMPANY] || []} editable={canEdit} onSave={saveField} />
          {f(FF.PARTNER_COMPANY) && (
            <EditableField label="Partner SPOC" value={f(FF.PARTNER_SPOC)} fieldId={FF.PARTNER_SPOC} type="select" options={selects[FF.PARTNER_SPOC] || []} editable={canEdit} onSave={saveField} />
          )}
          <EditableField label="RM Confidence" value={f(FF.RM_CONFIDENCE)} fieldId={FF.RM_CONFIDENCE} type="select" options={selects[FF.RM_CONFIDENCE] || ["25%", "50%", "75%", "90%"]} editable={canEdit} onSave={saveField} />
          <NextActionField value={f(FF.NEXT_ACTION)} stage={f(FF.LEAD_STAGE)} editable={canEdit} onSave={saveField} testid="overview-next-action" />
          <EditableField label="Consultation Taken By" value={f(FF.CONSULTATION_TAKEN_BY)} fieldId={FF.CONSULTATION_TAKEN_BY} type="select" options={selects[FF.CONSULTATION_TAKEN_BY] || []} editable={canEdit} onSave={saveField} />
          {f(FF.LEAD_STAGE) === "New Lead" && (
            <div data-testid="newlead-assign-note" className="text-xs text-[#92600a] bg-[#F5B71D]/10 border border-[#F5B71D]/30 rounded-lg px-3 py-2">
              Assign an HL RM by using <b>Change stage → Document Collection</b> (sets owner + confidence + quarter).
            </div>
          )}
          <EditableField label="Expected Disbursement Quarter" value={f(FF.EXPECTED_DISB_QUARTER)} fieldId={FF.EXPECTED_DISB_QUARTER} type="select" options={selects[FF.EXPECTED_DISB_QUARTER] || []} editable={canEdit} onSave={saveField} />
          <EditableField label="Disbursed Amount" value={f(FF.DISBURSED_AMOUNT)} fieldId={FF.DISBURSED_AMOUNT} type="number" editable={canEdit && disbursedEditable} onSave={saveField} render={(v) => formatINR(v)} />
          <EditableField label="Disbursement Date" value={f(FF.DISBURSEMENT_DATE)} fieldId={FF.DISBURSEMENT_DATE} type="date" editable={canEdit && disbursedEditable} onSave={saveField} render={(v) => fmtDate(v)} />
        </Section>

        <Section title="Property">
          {(f(FF.TRUVA_NONTRUVA) === "Yes" || !f(FF.TRUVA_NONTRUVA)) && <TruvaHouseLink c={c} canEdit={canEdit} onSaved={() => onStageChange(true)} />}
          <EditableField label="Micro Market" value={f(FF.MICRO_MARKET)} fieldId={FF.MICRO_MARKET} editable={false} onSave={saveField} />
          {(f(FF.TRUVA_NONTRUVA) !== "Yes") && <EditableField label="Property Name (Out of Truva)" value={f(FF.PROPERTY_NAME_OOT)} fieldId={FF.PROPERTY_NAME_OOT} editable={canEdit} onSave={saveField} />}
        </Section>

        <Section title="Follow-up">
          <EditableField label="Follow Up Date" value={f(FF.FOLLOW_UP_DATE)} fieldId={FF.FOLLOW_UP_DATE} type="date" editable={canEdit} onSave={saveField} render={(v) => fmtDate(v)} />
          <EditableField label="Status" value={f(FF.STATUS)} fieldId={FF.STATUS} type="select" options={selects[FF.STATUS] || []} editable={canEdit} onSave={saveField} />
          <EditableField label="Last Conversation" value={f(FF.LAST_CONVERSATION)} fieldId={FF.LAST_CONVERSATION} editable={false} onSave={saveField} render={(v) => fmtDate(v)} hint="last_conv" />
          <EditableField label="Lost Reason" value={f(FF.LOST_REASON)} fieldId={FF.LOST_REASON} type="select" options={selects[FF.LOST_REASON] || []} editable={canEdit} onSave={saveField} />
        </Section>

        <Section title="Lead source" testid="overview-lead-source">
          <EditableField label="Lead Source" value={f(FF.LEAD_SOURCE)} fieldId={FF.LEAD_SOURCE} editable={false} onSave={saveField} hint="lead_source" />
          <EditableField label="Campaign / Ad" value={[f("_attribution")?.utm_campaign, f("_attribution")?.ad_name].filter(Boolean).join(" · ") || null} fieldId="attribution" editable={false} onSave={saveField} />
          {f("_attribution")?.utm_source && <EditableField label="UTM source / medium" value={[f("_attribution")?.utm_source, f("_attribution")?.utm_medium].filter(Boolean).join(" / ")} fieldId="attribution2" editable={false} onSave={saveField} />}
        </Section>

        <Section title="Consultation" testid="overview-consultation">
          <EditableField label="Consultation Status" value={f(FF.CONSULTATION_STATUS)} fieldId={FF.CONSULTATION_STATUS} type="select" options={selects[FF.CONSULTATION_STATUS] || []} editable={canEdit && (selects[FF.CONSULTATION_STATUS] || []).length > 0} onSave={saveField} />
          <EditableField label="Consultation Call Date" value={f(FF.CONSULTATION_CALL_DATE)} fieldId={FF.CONSULTATION_CALL_DATE} editable={false} onSave={saveField} render={(v) => v ? (/T00:00(:00)?(\.000)?Z?$/.test(v) || !v.includes("T") ? fmtDate(v) : new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })) : "—"} />
          <EditableField label="Consultation Taken By" value={f(FF.CONSULTATION_TAKEN_BY)} fieldId={FF.CONSULTATION_TAKEN_BY} type="select" options={selects[FF.CONSULTATION_TAKEN_BY] || []} editable={canEdit} onSave={saveField} hint="consult_by" />
          <div className="flex flex-wrap gap-2 items-end pb-2">
            {f(FF.CONSULTATION_NOTES_LINK) ? <a href={f(FF.CONSULTATION_NOTES_LINK)} target="_blank" rel="noreferrer" data-testid="overview-consult-notes" className="text-xs text-[#38A2FF] border border-[#38A2FF]/30 rounded-lg px-3 py-1.5 hover:bg-[#38A2FF]/5">Consultation notes</a> : <span className="text-xs text-[#C5C4C4]">No notes link</span>}
            {f(FF.CONSULTATION_RECORDING_LINK) ? <a href={f(FF.CONSULTATION_RECORDING_LINK)} target="_blank" rel="noreferrer" data-testid="overview-consult-recording" className="text-xs text-[#8B46FF] border border-[#8B46FF]/30 rounded-lg px-3 py-1.5 hover:bg-[#8B46FF]/5">Recording</a> : <span className="text-xs text-[#C5C4C4]">No recording</span>}
          </div>
        </Section>

        <Section title="Notes">
          <div className="md:col-span-2">
            <EditableField label="Notes" value={f(FF.NOTES)} fieldId={FF.NOTES} type="textarea" editable={canEdit} onSave={saveField} />
          </div>
        </Section>
      </div>

      <div className="col-span-12 lg:col-span-4 space-y-6">
        <FactSheetCard onePager={c.one_pager} />
        {c.previous_cases?.length > 0 && (
          <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm">
            <h3 className="text-sm font-medium text-[#333333] mb-1">Previous cases for this number</h3>
            <p className="text-xs text-[#6B7280] mb-3">{c.previous_cases.length} linked</p>
            <div className="space-y-2">
              {c.previous_cases.map((p) => (
                <div key={p.record_id} className="flex items-center justify-between text-xs border border-[#E2E2E2] rounded-lg px-3 py-2" data-testid={`prev-case-${p.case_id}`}>
                  <span className="font-mono font-semibold text-[#E2521B]">{p.case_id}</span>
                  <StageChip stage={p.stage} />
                  <span className="text-[#6B7280]">{fmtDate(p.created_time)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <StageDialog open={stageOpen} onOpenChange={setStageOpen} recordId={recordId}
        truvaFlag={f(FF.TRUVA_NONTRUVA)} hasPropertyLink={(Array.isArray(c.fields?.[FF.PROPERTY_TRUVA]) && c.fields[FF.PROPERTY_TRUVA].length > 0) || !!c.property_truva_name} propertyText={f(FF.PROPERTY_NAME_OOT)}
        current={f(FF.LEAD_STAGE)} options={selects[FF.LEAD_STAGE] || []}
        lostOptions={selects[FF.LOST_REASON] || []}
        confidenceOptions={selects[FF.RM_CONFIDENCE] || ["25%", "50%", "75%", "90%"]}
        quarterOptions={selects[FF.EXPECTED_DISB_QUARTER] || []}
        rmOptions={selects[FF.HL_RM] || []}
        currentConfidence={f(FF.RM_CONFIDENCE)} currentQuarter={f(FF.EXPECTED_DISB_QUARTER)}
        currentRm={Array.isArray(f(FF.HL_RM)) ? f(FF.HL_RM)[0] : f(FF.HL_RM)}
        currentTakenBy={f(FF.CONSULTATION_TAKEN_BY)}
        expectedModified={c.modified_time} onDone={() => { setStageOpen(false); onStageChange(true); }} />
    </div>
  );
}

function FactSheetCard({ onePager }) {
  const link = Array.isArray(onePager) ? onePager[0]?.url : onePager;
  return (
    <div className="rounded-2xl border border-[#FF4802]/25 bg-gradient-to-br from-[#FF4802]/5 via-white to-[#F6F5EF] p-5 shadow-sm" data-testid="fact-sheet-card">
      <h3 className="text-sm font-medium text-[#333333] flex items-center gap-2 mb-3"><FileText className="w-4 h-4 text-[#FF4802]" /> Fact Sheet</h3>
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" data-testid="fact-sheet-open"
          className="inline-flex items-center gap-1.5 text-sm bg-[#FF4802] text-white px-4 py-2 rounded-lg hover:bg-[#E2521B] transition-colors font-medium">
          Open fact sheet <ExternalLink className="w-4 h-4" />
        </a>
      ) : (
        <p className="text-sm text-[#6B7280]">No fact sheet yet. Generate one from the Tools tab.</p>
      )}
    </div>
  );
}

const CONFIDENCE_REQUIRED = ["Document Collection", "Buyer Approval", "Logged-In", "IP/Sanction Issued", "Legal/Technical", "Final Sanction OR DD copy", "Part Disbursement to Seller's Loan"];

function StageDialog({ open, onOpenChange, recordId, current, options, lostOptions = [], confidenceOptions = [], quarterOptions = [], rmOptions = [], currentConfidence, currentQuarter, currentRm, currentTakenBy, expectedModified, onDone, truvaFlag, hasPropertyLink, propertyText }) {
  const [stage, setStage] = useState(current);
  const [note, setNote] = useState("");
  const [lost, setLost] = useState("");
  const [conf, setConf] = useState(currentConfidence || "");
  const [quar, setQuar] = useState(currentQuarter || "");
  const [hlrm, setHlrm] = useState(currentRm || currentTakenBy || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setStage(current); setNote(""); setLost(""); setConf(currentConfidence || ""); setQuar(currentQuarter || ""); setHlrm(currentRm || currentTakenBy || ""); } }, [open, current, currentConfidence, currentQuarter, currentRm, currentTakenBy]);

  const needsConf = CONFIDENCE_REQUIRED.includes(stage);
  const needsRm = stage === "Document Collection";
  // Hotfix 26 Oct: property gate — Truva → linked Closing CRM record; non-Truva → text; unknown → either. Filled inline, saved in the same request.
  const propKind = truvaFlag === "Yes" ? "truva" : (truvaFlag === "No" || /non/i.test(truvaFlag || "")) ? "oot" : "either";
  const propertyFilled = propKind === "truva" ? hasPropertyLink : propKind === "oot" ? !!propertyText : (hasPropertyLink || !!propertyText);
  const needsProperty = needsConf && stage !== LOST_STAGE && !propertyFilled;
  const [propQ, setPropQ] = useState(""); const [propRes, setPropRes] = useState([]); const [propId, setPropId] = useState(""); const [propName, setPropName] = useState(""); const [propText, setPropText] = useState("");
  useEffect(() => {
    if (!needsProperty || propKind === "oot") return undefined;
    const t = setTimeout(() => api.get("/closing-crm/search", { params: { q: propQ } }).then((r) => setPropRes(r.data.results)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [propQ, needsProperty, propKind]);
  const submit = async () => {
    if (stage === LOST_STAGE && !lost.trim()) { toast.error("Lost Reason is required"); return; }
    if (needsConf && (!conf || !quar)) { toast.error("RM Confidence and Expected Quarter are required from Document Collection onwards"); return; }
    if (needsRm && !hlrm) { toast.error("HL RM is required to move to Document Collection"); return; }
    if (needsProperty && !propId && !propText.trim()) { toast.error(propKind === "truva" ? "Link the Truva House property first" : propKind === "oot" ? "Property Name is required" : "Link a Truva House or enter the property name"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${recordId}/stage`, {
        stage, note: note || null, lost_reason: lost || null,
        rm_confidence: conf || null, expected_quarter: quar || null, hl_rm: hlrm || null,
        property_truva: propId || null, property_name: propText.trim() || null,
        expected_modified_time: expectedModified,
      });
      if (data.conflict) { toast.error("Case changed in Airtable — reopen to reload"); onDone(); return; }
      toast.success(`Stage → ${stage} · synced`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stage change failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl truva-popup-shadow" aria-describedby={undefined} data-testid="stage-dialog">
        <DialogHeader><DialogTitle className="text-[#333333] font-medium">Change stage</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={stage || ""} onValueChange={setStage}>
            <SelectTrigger data-testid="stage-select" className="border-[#E2E2E2] rounded-lg"><SelectValue placeholder="Select stage" /></SelectTrigger>
            <SelectContent className="bg-white rounded-xl border-[#E2E2E2]">
              {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          {needsRm && (
            <div data-testid="stage-hlrm-block">
              <label className="text-[11px] uppercase text-[#6B7280]">HL RM (owner)</label>
              <Select value={hlrm || ""} onValueChange={setHlrm}>
                <SelectTrigger data-testid="stage-hlrm" className="border-[#E2E2E2] rounded-lg"><SelectValue placeholder="HL RM (defaults to Consultation Taken By)" /></SelectTrigger>
                <SelectContent className="bg-white rounded-xl border-[#E2E2E2] max-h-60">{rmOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {needsConf && (
            <div className="grid grid-cols-2 gap-2" data-testid="stage-confidence-block">
              <Select value={conf || ""} onValueChange={setConf}>
                <SelectTrigger data-testid="stage-confidence" className="border-[#E2E2E2] rounded-lg"><SelectValue placeholder="RM Confidence" /></SelectTrigger>
                <SelectContent className="bg-white rounded-xl border-[#E2E2E2]">{confidenceOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={quar || ""} onValueChange={setQuar}>
                <SelectTrigger data-testid="stage-quarter" className="border-[#E2E2E2] rounded-lg"><SelectValue placeholder="Expected Quarter" /></SelectTrigger>
                <SelectContent className="bg-white rounded-xl border-[#E2E2E2]">{quarterOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {stage === LOST_STAGE && (
            <Select value={lost || ""} onValueChange={setLost}>
              <SelectTrigger data-testid="lost-reason-input" className="border-[#E2E2E2] rounded-lg"><SelectValue placeholder="Lost Reason (required)" /></SelectTrigger>
              <SelectContent className="bg-white rounded-xl border-[#E2E2E2]">
                {lostOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {needsProperty && (
            <div className="rounded-xl border border-[#FF4802]/30 bg-[#FF4802]/5 p-2 space-y-2" data-testid="stage-property-block">
              <div className="text-[11px] uppercase text-[#92600a]">Property required before {stage}</div>
              {propKind !== "oot" && (
                <div>
                  {propId ? <div className="text-xs text-[#333333] flex items-center gap-2" data-testid="stage-property-picked">🏠 {propName} <button onClick={() => { setPropId(""); setPropName(""); }} className="text-[11px] text-[#6B7280] underline">change</button></div> : (
                    <>
                      <input value={propQ} onChange={(e) => setPropQ(e.target.value)} placeholder="Search Truva House (Closing CRM) by name…" data-testid="stage-property-search" className="w-full text-xs border border-[#E2E2E2] rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#FF4802]/40" />
                      <div className="max-h-32 overflow-auto mt-1">{propRes.map((r) => <button key={r.id} onClick={() => { setPropId(r.id); setPropName(r.name); }} data-testid={`stage-property-pick-${r.id}`} className="block w-full text-left text-xs px-2 py-1 rounded hover:bg-[#F6F5EF]">{r.name}</button>)}</div>
                    </>
                  )}
                </div>
              )}
              {propKind !== "truva" && <input value={propText} onChange={(e) => setPropText(e.target.value)} placeholder="Property Name (Out of Truva)" data-testid="stage-property-name" className="w-full text-xs border border-[#E2E2E2] rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#FF4802]/40" />}
            </div>
          )}
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (appended to Case History)" data-testid="stage-note-input" className="border-[#E2E2E2] rounded-lg" />
        </div>
        <DialogFooter className="gap-2">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-lg border border-[#E2E2E2] text-sm text-[#333333] hover:bg-[#F7F7F7] transition-colors">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="stage-confirm" className="px-4 py-2 rounded-lg bg-[#FF4802] text-white text-sm hover:bg-[#E2521B] disabled:opacity-50 transition-colors font-medium">
            {saving ? "Saving…" : "Confirm & save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function HistoryTab({ c, recordId, onUpdate, f }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const history = f(FF.CASE_HISTORY) || "";

  const add = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${recordId}/history`, { note });
      onUpdate({ ...c, fields: { ...c.fields, [FF.CASE_HISTORY]: data.case_history } });
      setNote("");
      toast.success("Note appended · synced");
    } catch (e) { toast.error("Failed to add note"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-12 lg:col-span-8 space-y-4">
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-6 shadow-sm">
          <h3 className="text-sm font-medium text-[#333333] mb-3 flex items-center gap-2"><HistoryIcon className="w-4 h-4 text-[#FF4802]" /> Case History</h3>
          <div className="flex gap-2 mb-4">
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="text-sm border-[#E2E2E2] rounded-lg" data-testid="history-note-input" />
            <button onClick={add} disabled={saving} data-testid="history-add-button" className="px-4 rounded-lg bg-[#FF4802] text-white text-sm hover:bg-[#E2521B] disabled:opacity-50 self-stretch font-medium transition-colors">
              {saving ? "…" : "Add"}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-[#333333] font-sans leading-relaxed border-t border-[#E2E2E2] pt-3" data-testid="case-history-text">
            {history || "No history yet."}
          </pre>
        </div>
      </div>
    </div>
  );
}

function InternalTab({ f, selects, saveField, canEdit }) {
  return (
    <div className="rounded-2xl border border-[#F5B71D]/40 bg-[#F5B71D]/5 p-6 shadow-sm max-w-2xl">
      <h3 className="text-sm font-medium text-[#333333] mb-1 uppercase" style={{ letterSpacing: "0.06em" }}>Internal · Commission & Cashback</h3>
      <p className="text-xs text-[#92600a] mb-4">Never exposed to partners.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        <EditableField label="Total Commission" value={f(FF.TOTAL_COMMISSION)} fieldId={FF.TOTAL_COMMISSION} type="number" editable={canEdit} onSave={saveField} render={(v) => formatINR(v)} />
        <EditableField label="Cashback (yes/no)" value={f(FF.CASHBACK_YN) ? "Yes" : "No"} fieldId={FF.CASHBACK_YN} type="select" options={["Yes", "No"]} editable={canEdit}
          onSave={(fid, v) => saveField(fid, v === "Yes")} testid="cashback-yn" />
        <EditableField label="Cashback" value={f(FF.CASHBACK)} fieldId={FF.CASHBACK} type="number" editable={canEdit} onSave={saveField} render={(v) => formatINR(v)} />
        <EditableField label="Net Commission (formula)" value={f(FF.NET_COMMISSION)} fieldId={FF.NET_COMMISSION} editable={false} onSave={saveField} render={(v) => formatINR(v)} />
      </div>
    </div>
  );
}

function lookupVals(v) {
  if (v == null) return [];
  if (Array.isArray(v)) return v.filter((x) => x != null);
  if (typeof v === "object" && v.valuesByLinkedRecordId) {
    const ids = Array.isArray(v.linkedRecordIds) ? v.linkedRecordIds : Object.keys(v.valuesByLinkedRecordId);
    return ids.flatMap((id) => {
      const x = v.valuesByLinkedRecordId[id];
      return Array.isArray(x) ? x : [x];
    }).filter((x) => x != null);
  }
  return [v];
}

function fmtLookupDate(v) {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function TruvaHouseTab({ c, f }) {
  const regDate = fmtLookupDate(lookupVals(f(FF.REGISTRATION_DATE))[0]);
  const handover = fmtLookupDate(lookupVals(f(FF.BUYER_HANDOVER_DATE))[0]);
  const rmNames = [...new Set(lookupVals(f(FF.RM_FROM_PROPERTY))
    .map((v) => (v && typeof v === "object" ? v.name || v.email : v))
    .filter(Boolean))].join(", ");
  const buyerLawyer = lookupVals(f(FF.BUYER_LAWYER_LINK))
    .map((v) => (v && typeof v === "object" ? v.url : v))
    .find((v) => typeof v === "string" && /^https?:\/\//.test(v));
  const rows = [
    ["Registration Date", regDate, "th-reg-date"],
    ["Buyer Handover Date", handover, "th-handover-date"],
    ["Relationship Manager (Property)", rmNames || null, "th-property-rm"],
  ];
  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white p-6 shadow-sm space-y-5 max-w-3xl" data-testid="truva-house-tab">
      <h3 className="text-sm font-medium text-[#333333]" data-testid="th-property-name">Truva House — {c.property_truva_name || "Linked property"}</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        {rows.map(([k, v, tid]) => (
          <div key={k}><div className="text-[11px] uppercase text-[#6B7280]" style={{ letterSpacing: "0.06em" }}>{k}</div><div className="text-sm text-[#333333] mt-0.5" data-testid={tid}>{v || "—"}</div></div>
        ))}
      </div>
      <div>
        <div className="text-[11px] uppercase text-[#6B7280] mb-1" style={{ letterSpacing: "0.06em" }}>Buyer / Lawyer Link</div>
        {buyerLawyer
          ? <a href={buyerLawyer} target="_blank" rel="noreferrer" data-testid="th-buyer-lawyer" className="text-sm text-[#38A2FF] hover:underline">Open buyer/lawyer link</a>
          : <span className="text-sm text-[#C5C4C4]" data-testid="th-buyer-lawyer-empty">—</span>}
      </div>
      <TruvaAttRow label="Tri Party MoU" value={f(FF.TRI_PARTY_MOU)} testId="th-tri-party-mou" />
      <TruvaLinkRow label="Seller Side Documents" value={f(FF.SELLER_SIDE_DOCS)} linkLabel="Open seller documents" testId="th-seller-docs" />
    </div>
  );
}

function TruvaAttRow({ label, value, testId }) {
  const vals = lookupVals(value);
  const atts = vals.filter((a) => a && typeof a === "object" && a.url);
  const text = atts.length === 0 ? vals.map((a) => (a && typeof a === "object" ? a.name || a.email : a)).filter(Boolean).join(", ") : null;
  return (
    <div>
      <div className="text-[11px] uppercase text-[#6B7280] mb-1" style={{ letterSpacing: "0.06em" }}>{label}</div>
      {atts.length > 0
        ? <div className="space-y-1">{atts.map((a, i) => <a key={i} href={a.url} target="_blank" rel="noreferrer" data-testid={`${testId}-${i}`} className="block text-sm text-[#38A2FF] hover:underline">{a.filename || `File ${i + 1}`}</a>)}</div>
        : text ? <span className="text-sm text-[#333333]" data-testid={testId}>{text}</span>
        : <span className="text-sm text-[#C5C4C4]" data-testid={`${testId}-empty`}>—</span>}
    </div>
  );
}

function TruvaLinkRow({ label, value, linkLabel, testId }) {
  const links = lookupVals(value)
    .map((v) => (v && typeof v === "object" ? { url: v.url, name: v.filename } : { url: v }))
    .filter((x) => typeof x.url === "string" && /^https?:\/\//.test(x.url));
  return (
    <div>
      <div className="text-[11px] uppercase text-[#6B7280] mb-1" style={{ letterSpacing: "0.06em" }}>{label}</div>
      {links.length === 0
        ? <span className="text-sm text-[#C5C4C4]" data-testid={`${testId}-empty`}>—</span>
        : <div className="space-y-1">{links.map((x, i) => (
            <a key={i} href={x.url} target="_blank" rel="noreferrer" data-testid={links.length > 1 ? `${testId}-${i}` : testId} className="block text-sm text-[#38A2FF] hover:underline">
              {x.name || (links.length > 1 ? `${linkLabel} ${i + 1}` : linkLabel)}
            </a>))}
        </div>}
    </div>
  );
}

function ActionsTab({ c, f, recordId }) {
  const [reason, setReason] = useState("");
  const [toRm, setToRm] = useState("");
  const [saving, setSaving] = useState(false);
  const [rms, setRms] = useState([]);
  const [myReqs, setMyReqs] = useState([]);

  const loadReqs = useCallback(async () => {
    try {
      const [{ data: rmData }, { data: reqData }] = await Promise.all([
        api.get("/meta/rms"),
        api.get("/reassignments", { params: { status: "all" } }),
      ]);
      setRms(rmData.rms || []);
      setMyReqs((reqData.requests || []).filter((r) => r.case_record_id === recordId));
    } catch (e) { /* ignore */ }
  }, [recordId]);
  useEffect(() => { loadReqs(); }, [loadReqs]);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setSaving(true);
    try {
      await api.post("/reassignments", { case_record_id: recordId, case_id: c.case_id, to_rm: toRm || null, reason });
      toast.success("Reassignment requested — sent to manager queue");
      setReason(""); setToRm(""); loadReqs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
    finally { setSaving(false); }
  };

  const STATUS_CLS = { pending: "text-[#92600a] bg-[#F5B71D]/15", approved: "text-[#159A50] bg-[#159A50]/10", rejected: "text-[#CA1212] bg-[#CA1212]/10" };

  return (
    <div className="space-y-6 max-w-xl">
      <div className="rounded-2xl border border-[#E2E2E2] bg-white p-6 shadow-sm">
        <h3 className="text-xs font-medium text-[#6B7280] mb-4 uppercase" style={{ letterSpacing: "0.06em" }}>Request reassignment</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-[#6B7280] mb-1.5">Target RM (optional)</label>
            <Select value={toRm || "none"} onValueChange={(v) => setToRm(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="reassign-target-input" className="border-[#E2E2E2] rounded-lg"><SelectValue placeholder="Any available RM" /></SelectTrigger>
              <SelectContent className="bg-white rounded-xl border-[#E2E2E2] max-h-60">
                <SelectItem value="none">Any available RM</SelectItem>
                {rms.map((r) => <SelectItem key={r.hl_rm_value} value={r.hl_rm_value}>{r.name} ({r.hl_rm_value})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="reassign-reason-input" className="border-[#E2E2E2] rounded-lg" />
          <button onClick={submit} disabled={saving} data-testid="reassign-submit" className="px-4 py-2 rounded-lg bg-[#FF4802] text-white text-sm hover:bg-[#E2521B] disabled:opacity-50 font-medium transition-colors">
            {saving ? "Submitting…" : "Request reassignment"}
          </button>
        </div>
      </div>

      {myReqs.length > 0 && (
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-6 shadow-sm" data-testid="reassign-history">
          <h3 className="text-xs font-medium text-[#6B7280] mb-3 uppercase" style={{ letterSpacing: "0.06em" }}>Reassignment requests for this case</h3>
          <div className="space-y-2">
            {myReqs.map((r) => (
              <div key={r.id} className="border border-[#E2E2E2] rounded-lg px-3 py-2 text-sm" data-testid={`reassign-status-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[#333333]">{r.to_rm ? `→ ${r.to_rm}` : "Any RM"}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_CLS[r.status] || ""}`}>{r.status}</span>
                </div>
                <div className="text-xs text-[#6B7280] mt-0.5">{r.reason}</div>
                {r.status === "rejected" && r.note && (
                  <div className="text-xs text-[#CA1212] mt-1">Manager note: {r.note}</div>
                )}
                {r.status === "approved" && (
                  <div className="text-xs text-[#159A50] mt-1">Approved{r.note ? ` · ${r.note}` : ""}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function ToolsTab({ c, f }) {
  const iframeRef = React.useRef(null);

  const prefill = useMemo(() => {
    const banks = f(FF.BANK_NAME);
    return {
      case_id: c.case_id,
      borrower: f(FF.BORROWER) || f(FF.NAME) || "",
      co_borrower: f(FF.CO_BORROWER) || "",
      phone: f(FF.PHONE) || "",
      email: f(FF.EMAIL) || "",
      loan_amount: f(FF.LOAN_AMOUNT) || "",
      loan_tenure: f(FF.LOAN_TENURE) || "",
      agreement_value: f(FF.AGREEMENT_VALUE) || "",
      bank_name: Array.isArray(banks) ? banks.join(", ") : (banks || ""),
      property: f(FF.PROPERTY_TRUVA) || f(FF.PROPERTY_NAME_OOT) || "",
      stage: f(FF.LEAD_STAGE) || "",
    };
  }, [c, f]);

  const sendPrefill = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (win) win.postMessage({ type: "TRUVA_CASE_PREFILL", payload: prefill }, window.location.origin);
  }, [prefill]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return; // same-app origin only
      const data = e.data || {};
      if (data.type === "TRUVA_TOOL_READY") sendPrefill();
      if (data.type === "TRUVA_TOOL_RESULT") {
        toast.success(`${data.tool || "Tool"} result received from embedded tools`);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [sendPrefill]);

  return (
    <div className="space-y-3" data-testid="tools-embed">
      <div className="rounded-xl border border-[#159A50]/30 bg-[#159A50]/5 px-4 py-3 text-sm text-[#333333] flex items-center gap-2" data-testid="scanline-moved-notice">
        <span className="font-medium text-[#159A50]">Document check has moved</span>
        <span className="text-[#6B7280]">— run it from the</span>
        <button onClick={() => { const el = document.querySelector('[data-testid="ws-tab-documents"]'); if (el) el.click(); }}
          className="text-[#FF4802] font-medium hover:underline" data-testid="goto-documents-check">Documents tab → Document check</button>
      </div>
      <div className="rounded-2xl border border-[#E2E2E2] bg-white overflow-hidden shadow-sm">
        <iframe
          ref={iframeRef}
          title="Truva HL Tools"
          src="/tools/index.html"
          onLoad={sendPrefill}
          data-testid="tools-iframe"
          className="w-full"
          style={{ height: "calc(100vh - 360px)", minHeight: 520, border: "none" }}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
      </div>
    </div>
  );
}
